/*

    PinchZoom.js
    Copyright (c) Manuel Stofer 2013 - today

    Author: Manuel Stofer (mst@rtp.ch)
    Version: 2.3.4

    Permission is hereby granted, free of charge, to any person obtaining a copy
    of this software and associated documentation files (the "Software"), to deal
    in the Software without restriction, including without limitation the rights
    to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
    copies of the Software, and to permit persons to whom the Software is
    furnished to do so, subject to the following conditions:

    The above copyright notice and this permission notice shall be included in
    all copies or substantial portions of the Software.

    THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
    IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
    FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
    AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
    LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
    OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
    THE SOFTWARE.

*/

// utils
const sum = function (a, b) {
    return a + b;
};

const isCloseTo = function (value, expected) {
    return value > expected - 0.01 && value < expected + 0.01;
};

/**
 * Pinch zoom
 * @param el
 * @param options
 * @constructor
 */
export default function PinchZoom(el, options) {
    this.el = el;
    this.initValues();
    this.options = { ...this.defaults, ...options };
    this.setupMarkup();
    this.bindEvents();
    this.update();

    // The image may already be loaded when PinchZoom is initialized,
    // and then the load event (which trigger update) will never fire.
    if (this.isImageLoaded(this.el)) {
        this.setupOffsets();
    }
}

PinchZoom.prototype = {
    defaults: {
        tapZoomFactor: 4,
        zoomOutFactor: 1.3,
        animationDuration: 300,
        maxZoom: 4,
        minZoom: 1,
        draggableUnzoomed: false,
        lockDragAxis: false,
        setOffsetsOnce: false,
        use2d: true,
        useMouseWheel: true,
        verticalPadding: 0,
        horizontalPadding: 0,
        mouseWheelZoomFactor: 3.5,
        zoomStartEventName: "pz_zoomstart",
        zoomUpdateEventName: "pz_zoomupdate",
        zoomEndEventName: "pz_zoomend",
        dragStartEventName: "pz_dragstart",
        dragUpdateEventName: "pz_dragupdate",
        dragEndEventName: "pz_dragend",
        pointerZoomEventName: "pz_pointerzoom",
        mouseWheelEventName: "pz_mousewheel",
        updateEventName: "pz_update",
        animationStartEventName: "pz_animation_start",
        animationEndEventName: "pz_animation_end"
    },

    initValues() {
        this.zoomFactor = 1;
        this.lastScale = 1;
        this.offset = {
            x: 0,
            y: 0
        };
        this.initialOffset = {
            x: 0,
            y: 0
        };
    },

    get element() {
        return this.el;
    },

    getOptionValue(option) {
        return this.options[option];
    },

    get zoomed() {
        return this.zoomFactor !== 1;
    },

    /**
     * Event handler for 'dragstart'
     * @param event
     */
    handleDragStart: function (event) {
        if (!this.zoomed) return;
        this.el.dispatchEvent(new CustomEvent(this.options.dragStartEventName));
        this.el.dataset.dragging = "";
        this.stopAnimation();
        this.lastDragPosition = false;
        this.hasInteraction = true;
        this.handleDrag(event);
    },

    /**
     * Event handler for 'drag'
     * @param event
     */
    handleDrag: function (event) {
        if (!this.zoomed || (event.type !== "touchmove" && event.type !== "mousemove")) return;
        let touch = event.type === "touchmove" ? this.getTouches(event)[0] : this.getPointer(event);
        this.drag(touch, this.lastDragPosition);
        this.offset = this.sanitizeOffset(this.offset);
        this.lastDragPosition = touch;
    },

    handleDragEnd: function () {
        if (!this.zoomed) return;
        this.el.dispatchEvent(new CustomEvent(this.options.dragEndEventName));
        delete this.el.dataset.dragging;
        this.end();
    },

    /**
     * Event handler for 'zoomstart'
     */
    handleZoomStart: function () {
        this.el.dispatchEvent(new CustomEvent(this.options.zoomStartEventName));
        this.stopAnimation();
        this.lastScale = 1;
        this.nthZoom = 0;
        this.lastZoomCenter = false;
        this.hasInteraction = true;
    },

    /**
     * Event handler for 'zoom'
     * @param event
     * @param newScale
     */
    handleZoom: function (event, newScale) {
        this.el.dispatchEvent(
            new CustomEvent(this.options.animationStartEventName, {
                detail: {
                    handler: this,
                    action: "pinch-zoom"
                }
            })
        );
        // a relative scale factor is used
        let touchCenter = this.getTouchCenter(this.getTouches(event)),
            scale = newScale / this.lastScale;
        this.lastScale = newScale;

        // the first touch events are thrown away since they are not precise
        this.nthZoom += 1;
        if (this.nthZoom > 3) {
            this.scale(scale, touchCenter);
            this.drag(touchCenter, this.lastZoomCenter);
        }
        this.lastZoomCenter = touchCenter;
    },

    handleZoomEnd: function () {
        this.el.dispatchEvent(new CustomEvent(this.options.zoomEndEventName));
        this.end();
        this.el.dispatchEvent(
            new CustomEvent(this.options.animationEndEventName, {
                detail: {
                    handler: this,
                    action: "pinch-zoom"
                }
            })
        );
    },

    /**
     * Event handler for click/tap zoom
     * @param event
     */
    handlePointerZoom: function (event) {
        let center = event.type === "touchstart" ? this.getTouches(event)[0] : this.getPointer(event),
            zoomFactor = this.zoomFactor > 1 ? 1 : this.options.tapZoomFactor,
            startZoomFactor = this.zoomFactor,
            updateProgress = function (progress) {
                this.scaleTo(startZoomFactor + progress * (zoomFactor - startZoomFactor), center);
            }.bind(this);
        this.lastScale = zoomFactor / this.zoomFactor;

        if (this.hasInteraction) {
            return;
        }

        if (startZoomFactor > zoomFactor) {
            center = this.getCurrentZoomCenter();
        }

        this.el.dispatchEvent(
            new CustomEvent(this.options.animationStartEventName, {
                detail: {
                    handler: this,
                    action: "pointer-zoom"
                }
            })
        );
        this.animate(this.options.animationDuration, updateProgress, this.swing, () => {
            this.el.dispatchEvent(
                new CustomEvent(this.options.animationEndEventName, {
                    detail: {
                        handler: this,
                        action: "pointer-zoom"
                    }
                })
            );
        });
        this.el.dispatchEvent(new CustomEvent(this.options.pointerZoomEventName));
    },

    /**
     * Event handler for 'mousewheel'
     * @param event
     */
    handleMouseWheel: function (event) {
        this.el.dispatchEvent(
            new CustomEvent(this.options.animationStartEventName, {
                detail: {
                    handler: this,
                    action: "mousewheel"
                }
            })
        );
        let center = this.getPointer(event),
            newScale = Math.min(
                Math.max(
                    this.options.minZoom,
                    this.zoomFactor + (event.deltaY / this.options.mouseWheelZoomFactor) * -0.01
                ),
                this.options.maxZoom
            ),
            scale = newScale / this.zoomFactor,
            handleEnd = false;
        this.scale(scale, center);

        if (newScale === 1 && this.lastScale !== 1) {
            handleEnd = true;
        }

        this.lastScale = newScale;
        this.update();

        this.el.dispatchEvent(new CustomEvent(this.options.mouseWheelEventName));

        if (handleEnd) {
            this.handleZoomEnd(event);
        }
        this.el.dispatchEvent(
            new CustomEvent(this.options.animationEndEventName, {
                detail: {
                    handler: this,
                    action: "mousewheel"
                }
            })
        );
    },

    /**
     * reset to initial scale and position
     */
    reset() {
        if (this.zoomed) {
            this.el.dispatchEvent(
                new CustomEvent(this.options.animationStartEventName, {
                    detail: {
                        handler: this,
                        action: "reset"
                    }
                })
            );
            this.el.addEventListener(
                "transitionend",
                () => {
                    this.el.style.transition = "";
                    this.el.dispatchEvent(
                        new CustomEvent(this.options.animationEndEventName, {
                            detail: {
                                handler: this,
                                action: "reset"
                            }
                        })
                    );
                },
                { once: true }
            );
            this.el.style.transition = `transform ${this.options.animationDuration}ms ease-in-out`;
            this.el.style.transform = "scale(1,1) translate(0px)";
            this.initValues();
            this.el.dispatchEvent(
                new CustomEvent(this.options.updateEventName, {
                    detail: {
                        zoomFactor: 1,
                        offsetX: 0,
                        offsetY: 0
                    }
                })
            );
        }
    },

    /**
     * Compute the initial offset
     *
     * the element should be centered in the container upon initialization
     */
    computeInitialOffset: function () {
        this.initialOffset = {
            x: -Math.abs(this.el.offsetWidth * this.getInitialZoomFactor() - this.container.offsetWidth) / 2,
            y: -Math.abs(this.el.offsetHeight * this.getInitialZoomFactor() - this.container.offsetHeight) / 2
        };
    },

    /**
     * Reset current image offset to that of the initial offset
     */
    resetOffset: function () {
        this.offset.x = this.initialOffset.x;
        this.offset.y = this.initialOffset.y;
    },

    /**
     * Determine if image is loaded
     */
    isImageLoaded: function (el) {
        if (el.nodeName === "IMG") {
            return el.complete && el.naturalHeight !== 0;
        } else {
            return Array.from(el.querySelectorAll("img")).every(this.isImageLoaded);
        }
    },

    setupOffsets: function () {
        if (this.options.setOffsetsOnce && this._isOffsetsSet) {
            return;
        }

        this._isOffsetsSet = true;

        this.computeInitialOffset();
        this.resetOffset();
    },

    /**
     * Max / min values for the offset
     * @param offset
     * @return {Object} the sanitized offset
     */
    sanitizeOffset: function (offset) {
        let elWidth = this.el.offsetWidth * this.getInitialZoomFactor() * this.zoomFactor;
        let elHeight = this.el.offsetHeight * this.getInitialZoomFactor() * this.zoomFactor;
        let maxX = elWidth - this.getContainerX() + this.options.horizontalPadding,
            maxY = elHeight - this.getContainerY() + this.options.verticalPadding,
            maxOffsetX = Math.max(maxX, 0),
            maxOffsetY = Math.max(maxY, 0),
            minOffsetX = Math.min(maxX, 0) - this.options.horizontalPadding,
            minOffsetY = Math.min(maxY, 0) - this.options.verticalPadding;

        return {
            x: Math.min(Math.max(offset.x, minOffsetX), maxOffsetX),
            y: Math.min(Math.max(offset.y, minOffsetY), maxOffsetY)
        };
    },

    /**
     * Scale to a specific zoom factor (not relative)
     * @param zoomFactor
     * @param center
     */
    scaleTo: function (zoomFactor, center) {
        this.scale(zoomFactor / this.zoomFactor, center);
    },

    /**
     * Scales the element from specified center
     * @param scale
     * @param center
     */
    scale: function (scale, center) {
        scale = this.scaleZoomFactor(scale);
        this.addOffset({
            x: (scale - 1) * (center.x + this.offset.x),
            y: (scale - 1) * (center.y + this.offset.y)
        });
        this.el.dispatchEvent(new CustomEvent(this.options.zoomUpdateEventName));
    },

    /**
     * Scales the zoom factor relative to current state
     * @param scale
     * @return the actual scale (can differ because of max min zoom factor)
     */
    scaleZoomFactor: function (scale) {
        let originalZoomFactor = this.zoomFactor;
        this.zoomFactor *= scale;
        this.zoomFactor = Math.min(this.options.maxZoom, Math.max(this.zoomFactor, this.options.minZoom));
        return this.zoomFactor / originalZoomFactor;
    },

    /**
     * Determine if the image is in a draggable state
     *
     * When the image can be dragged, the drag event is acted upon and cancelled.
     * When not draggable, the drag event bubbles through this component.
     *
     * @return {Boolean}
     */
    canDrag: function () {
        return this.options.draggableUnzoomed || !isCloseTo(this.zoomFactor, 1);
    },

    /**
     * Drags the element
     * @param center
     * @param lastCenter
     */
    drag: function (center, lastCenter) {
        if (lastCenter) {
            if (this.options.lockDragAxis) {
                // lock scroll to position that was changed the most
                if (Math.abs(center.x - lastCenter.x) > Math.abs(center.y - lastCenter.y)) {
                    this.addOffset({
                        x: -(center.x - lastCenter.x),
                        y: 0
                    });
                } else {
                    this.addOffset({
                        y: -(center.y - lastCenter.y),
                        x: 0
                    });
                }
            } else {
                this.addOffset({
                    y: -(center.y - lastCenter.y),
                    x: -(center.x - lastCenter.x)
                });
            }
            this.el.dispatchEvent(new CustomEvent(this.options.dragUpdateEventName));
        }
    },

    /**
     * Calculates the touch center of multiple touches
     * @param touches
     * @return {Object}
     */
    getTouchCenter: function (touches) {
        return this.getVectorAvg(touches);
    },

    /**
     * Calculates the average of multiple vectors (x, y values)
     */
    getVectorAvg: function (vectors) {
        return {
            x:
                vectors
                    .map(function (v) {
                        return v.x;
                    })
                    .reduce(sum) / vectors.length,
            y:
                vectors
                    .map(function (v) {
                        return v.y;
                    })
                    .reduce(sum) / vectors.length
        };
    },

    /**
     * Adds an offset
     * @param offset the offset to add
     * @return return true when the offset change was accepted
     */
    addOffset: function (offset) {
        this.offset = {
            x: this.offset.x + offset.x,
            y: this.offset.y + offset.y
        };
    },

    sanitize: function () {
        if (this.zoomFactor < this.options.zoomOutFactor && this.zoomFactor !== 1) {
            this.zoomOutAnimation();
        } else if (this.isInsaneOffset(this.offset)) {
            this.sanitizeOffsetAnimation();
        }
    },

    /**
     * Checks if the offset is ok with the current zoom factor
     * @param offset
     * @return {Boolean}
     */
    isInsaneOffset: function (offset) {
        let sanitizedOffset = this.sanitizeOffset(offset);
        return sanitizedOffset.x !== offset.x || sanitizedOffset.y !== offset.y;
    },

    /**
     * Creates an animation moving to a sane offset
     */
    sanitizeOffsetAnimation: function () {
        let targetOffset = this.sanitizeOffset(this.offset),
            startOffset = {
                x: this.offset.x,
                y: this.offset.y
            },
            updateProgress = function (progress) {
                this.offset.x = startOffset.x + progress * (targetOffset.x - startOffset.x);
                this.offset.y = startOffset.y + progress * (targetOffset.y - startOffset.y);
                this.update();
            }.bind(this);

        this.animate(this.options.animationDuration, updateProgress, this.swing);
    },

    /**
     * Zooms back to the original position,
     * (no offset and zoom factor 1)
     */
    zoomOutAnimation: function () {
        if (this.zoomFactor === 1) {
            return;
        }

        let startZoomFactor = this.zoomFactor,
            zoomFactor = 1,
            center = this.getCurrentZoomCenter(),
            updateProgress = function (progress) {
                this.scaleTo(startZoomFactor + progress * (zoomFactor - startZoomFactor), center);
            }.bind(this);

        this.el.dispatchEvent(
            new CustomEvent(this.options.animationStartEventName, {
                detail: {
                    handler: this,
                    action: "zoom-out"
                }
            })
        );
        this.animate(this.options.animationDuration, updateProgress, this.swing, () => {
            this.el.dispatchEvent(
                new CustomEvent(this.options.animationEndEventName, {
                    detail: {
                        handler: this,
                        action: "zoom-out"
                    }
                })
            );
        });
    },

    /**
     * Calculates the initial zoom factor (for the element to fit into the container)
     * @return {number} the initial zoom factor
     */
    getInitialZoomFactor: function () {
        let xZoomFactor = this.container.offsetWidth / this.el.offsetWidth;
        let yZoomFactor = this.container.offsetHeight / this.el.offsetHeight;

        return Math.min(xZoomFactor, yZoomFactor);
    },

    /**
     * Calculates the aspect ratio of the element
     * @return the aspect ratio
     */
    getAspectRatio: function () {
        return this.el.offsetWidth / this.el.offsetHeight;
    },

    /**
     * Calculates the virtual zoom center for the current offset and zoom factor
     * (used for reverse zoom)
     * @return {Object} the current zoom center
     */
    getCurrentZoomCenter: function () {
        let offsetLeft = this.offset.x - this.initialOffset.x;
        let centerX = -1 * this.offset.x - offsetLeft / (1 / this.zoomFactor - 1);

        let offsetTop = this.offset.y - this.initialOffset.y;
        let centerY = -1 * this.offset.y - offsetTop / (1 / this.zoomFactor - 1);

        return {
            x: centerX,
            y: centerY
        };
    },

    /**
     * Returns the touches of an event relative to the container offset
     * @param event
     * @return array touches
     */
    getTouches: function (event) {
        let rect = this.container.getBoundingClientRect();
        let scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
        let scrollLeft = document.documentElement.scrollLeft || document.body.scrollLeft;
        let posTop = rect.top + scrollTop;
        let posLeft = rect.left + scrollLeft;

        return Array.from(event.touches).map(function (touch) {
            return {
                x: touch.pageX - posLeft,
                y: touch.pageY - posTop
            };
        });
    },

    /**
     * Returns the pointer of an event relative to the container offset
     * @param event
     * @return {Object.<Number: x, Number: y>}
     */
    getPointer: function (event) {
        let rect = this.container.getBoundingClientRect();
        let scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
        let scrollLeft = document.documentElement.scrollLeft || document.body.scrollLeft;
        let posTop = rect.top + scrollTop;
        let posLeft = rect.left + scrollLeft;

        return {
            x: event.pageX - posLeft,
            y: event.pageY - posTop
        };
    },

    /**
     * Animation loop
     * does not support simultaneous animations
     * @param duration
     * @param framefn
     * @param timefn
     * @param callback
     */
    animate: function (duration, framefn, timefn, callback) {
        let startTime = new Date().getTime(),
            renderFrame = function () {
                if (!this.inAnimation) {
                    return;
                }
                let frameTime = new Date().getTime() - startTime,
                    progress = frameTime / duration;
                if (frameTime >= duration) {
                    framefn(1);
                    if (callback) {
                        callback();
                    }
                    this.update();
                    this.stopAnimation();
                    this.update();
                } else {
                    if (timefn) {
                        progress = timefn(progress);
                    }
                    framefn(progress);
                    this.update();
                    requestAnimationFrame(renderFrame);
                }
            }.bind(this);
        this.inAnimation = true;
        requestAnimationFrame(renderFrame);
    },

    /**
     * Stops the animation
     */
    stopAnimation: function () {
        this.inAnimation = false;
    },

    /**
     * Swing timing function for animations
     * @param p
     * @return {Number}
     */
    swing: function (p) {
        return -Math.cos(p * Math.PI) / 2 + 0.5;
    },

    getContainerX: function () {
        return this.container.offsetWidth;
    },

    getContainerY: function () {
        return this.container.offsetHeight;
    },

    /**
     * Creates the expected html structure
     */
    setupMarkup: function () {
        const parent = this.el.parentNode;
        const el = this.el;

        if (this.el.dataset.zoomSrc && this.el.dataset.zoomSrcset && this.el.dataset.zoomSizes) {
            this.orig = this.el;
            this.el = new Image();

            this.el.src = el.dataset.zoomSrc;
            this.el.srcset = el.dataset.zoomSrcset;
            this.el.sizes = el.dataset.zoomSizes;

            this.el.draggable = false;
            this.orig.style.display = "none";
        }

        this.container = document.createElement("div");
        this.container.classList.add("pinch-zoom-container");
        parent.insertBefore(this.container, el);
        this.container.appendChild(this.el);

        this.el.style.transformOrigin = "0% 0%";
        this.el.style.backfaceVisibility = "hidden";
        this.el.style.willChange = "transform";
    },

    end: function () {
        this.hasInteraction = false;
        this.sanitize();
        this.update();
    },

    /**
     * Binds all required event listeners
     */
    bindEvents: function () {
        this.eventHandler = new EventHandler(this);
        this.eventHandler.attach();

        this.resizeHandler = this.update.bind(this);
        window.addEventListener("resize", this.resizeHandler);
        Array.from(this.el.querySelectorAll("img")).forEach((imgEl) => {
            imgEl.addEventListener("load", this.resizeHandler);
        });

        if (this.el.nodeName === "IMG") {
            this.el.addEventListener("load", this.resizeHandler);
        }
    },

    /**
     * Updates the css values according to the current zoom factor and offset
     */
    update: function (event) {
        if (event && ["resize", "load"].includes(event.type)) {
            setTimeout(() => {
                this.setupOffsets();
            }, 0);
        }

        if (this.updatePlanned) {
            return;
        }
        this.updatePlanned = true;

        window.setTimeout(() => {
            let zoomFactor = this.getInitialZoomFactor() * this.zoomFactor,
                offsetX = Math.round(-this.offset.x / zoomFactor),
                offsetY = Math.round(-this.offset.y / zoomFactor),
                transform3d = `scale3d(${zoomFactor}, ${zoomFactor} ,1) translate3d(${offsetX}px, ${offsetY}px, 0px)`,
                transform2d = `scale(${zoomFactor}, ${zoomFactor}) translate(${offsetX}px, ${offsetY}px)`;

            // Scale 3d and translate3d are faster (at least on ios)
            // but they also reduce the quality.
            // PinchZoom uses the 3d transformations during interactions
            // after interactions it falls back to 2d transformations
            if (!this.options.use2d || this.hasInteraction || this.inAnimation) {
                this.is3d = true;
                this.el.style.transform = transform3d;
            } else {
                this.el.style.transform = transform2d;
                this.is3d = false;
            }
            this.el.dispatchEvent(
                new CustomEvent(this.options.updateEventName, {
                    detail: {
                        zoomFactor,
                        offsetX,
                        offsetY
                    }
                })
            );
            this.updatePlanned = false;
        }, 0);
    },

    /**
     * Enables event handling for gestures
     */
    enable: function () {
        this.enabled = true;
    },

    /**
     * Disables event handling for gestures
     */
    disable: function () {
        this.enabled = false;
    },

    /**
     * Unmounts the zooming container and global event listeners
     */
    destroy: function () {
        window.removeEventListener("resize", this.resizeHandler);

        if (this.container) {
            this.eventHandler.detach();
            Array.from(this.el.querySelectorAll("img")).forEach((imgEl) => {
                imgEl.removeEventListener("load", this.resizeHandler);
            });

            if (this.el.nodeName === "IMG") {
                this.el.removeEventListener("load", this.resizeHandler);
            }

            if (this.orig) {
                this.el.remove();
                this.el = this.orig;
                delete this.orig;
            }

            this.el.style.transform = "";
            this.el.style.transformOrigin = "";
            this.el.style.backfaceVisibility = "";
            this.el.style.willChange = "";
            this.el.style.display = "";
            this.container.parentNode.insertBefore(this.el, this.container);
            this.container.remove();
            this.container = null;
        }
    }
};

class EventHandler {
    interaction = null;
    fingers = 0;
    lastTouchStart = null;
    lastClickStart = null;
    startTouches = null;
    target = null;
    firstMove = true;

    interactions = {
        ZOOM: "zoom",
        DRAG: "drag"
    };

    constructor(target) {
        this.target = target;
        this.startHandler = this.handleStart.bind(this);
        this.touchMoveHandler = this.handleTouchMove.bind(this);
        this.touchEndHandler = this.handleTouchEnd.bind(this);
        this.wheelHandler = this.handleWheel.bind(this);
        this.mouseDownHandler = this.handleMouseDown.bind(this);
        this.mouseMoveHandler = this.handleMouseMove.bind(this);
        this.mouseUpHandler = this.handleMouseUp.bind(this);
    }

    attach() {
        this.target.container.addEventListener("touchstart", this.startHandler, { passive: false });
        this.target.container.addEventListener("touchmove", this.touchMoveHandler, { passive: false });
        this.target.container.addEventListener("touchend", this.touchEndHandler);
        if (this.target.options.useMouseWheel) {
            this.target.container.addEventListener("wheel", this.wheelHandler, { passive: false });
            this.target.container.addEventListener("mousedown", this.mouseDownHandler, { passive: true });
            this.target.container.addEventListener("mousemove", this.mouseMoveHandler, { passive: false });
            this.target.container.addEventListener("mouseup", this.mouseUpHandler, { passive: true });
        }
    }

    detach() {
        this.target.container.removeEventListener("touchstart", this.startHandler, { passive: false });
        this.target.container.removeEventListener("touchmove", this.touchMoveHandler, { passive: false });
        this.target.container.removeEventListener("touchend", this.touchEndHandler);
        if (this.target.options.useMouseWheel) {
            this.target.container.removeEventListener("wheel", this.wheelHandler);
            this.target.container.removeEventListener("mousedown", this.mouseDownHandler, { passive: true });
            this.target.container.removeEventListener("mousemove", this.mouseMoveHandler, { passive: false });
            this.target.container.removeEventListener("mouseup", this.mouseUpHandler, { passive: true });
        }
    }

    setInteraction(newInteraction, event) {
        if (this.interaction !== newInteraction) {
            if (this.interaction && !newInteraction) {
                switch (this.interaction) {
                    case this.interactions.ZOOM:
                        this.target.handleZoomEnd(event);
                        break;
                    case this.interactions.DRAG:
                        this.target.handleDragEnd(event);
                        break;
                }
            }

            switch (newInteraction) {
                case this.interactions.ZOOM:
                    this.target.handleZoomStart(event);
                    break;
                case this.interactions.DRAG:
                    this.target.handleDragStart(event);
                    break;
            }
        }
        this.interaction = newInteraction;
    }

    updateInteraction(event) {
        if (this.fingers === 2) {
            this.setInteraction(this.interactions.ZOOM);
        } else if (this.fingers === 1 && this.target.canDrag()) {
            this.setInteraction(this.interactions.DRAG, event);
        } else {
            this.setInteraction(null, event);
        }
    }

    targetTouches(touches) {
        return Array.from(touches).map(function (touch) {
            return {
                x: touch.pageX,
                y: touch.pageY
            };
        });
    }

    getDistance(a, b) {
        let x, y;
        x = a.x - b.x;
        y = a.y - b.y;
        return Math.sqrt(x * x + y * y);
    }

    calculateScale(startTouches, endTouches) {
        let startDistance = this.getDistance(startTouches[0], startTouches[1]),
            endDistance = this.getDistance(endTouches[0], endTouches[1]);
        return endDistance / startDistance;
    }

    cancelEvent(event) {
        event.stopPropagation();
        event.preventDefault();
    }

    handleStart(event) {
        if (!this.target.enabled) return;
        this.firstMove = true;
        this.fingers = event.touches.length;
    }

    handleTouchMove(event) {
        if (!this.target.enabled) return;
        if (this.firstMove) {
            this.updateInteraction(event);
            if (this.interaction) {
                this.cancelEvent(event);
            }
            this.startTouches = this.targetTouches(event.touches);
        } else {
            switch (this.interaction) {
                case this.interactions.ZOOM:
                    if (this.startTouches.length === 2 && event.touches.length === 2) {
                        this.target.handleZoom(
                            event,
                            this.calculateScale(this.startTouches, this.targetTouches(event.touches))
                        );
                    }
                    break;
                case this.interactions.DRAG:
                    this.target.handleDrag(event);
                    break;
            }
            if (this.interaction) {
                this.cancelEvent(event);
                this.target.update();
            }
        }

        this.firstMove = false;
    }

    handleTouchEnd(event) {
        if (!this.target.enabled) return;
        this.fingers = event.touches.length;
        if (this.fingers === 1 && !this.interaction && !this.target.inAnimation) {
            this.target.handlePointerZoom(event);
        }
        this.fingers = 0;
        this.updateInteraction(event);
    }

    handleWheel(event) {
        if (!this.target.enabled) return;
        this.cancelEvent(event);
        this.target.handleMouseWheel(event);
    }

    handleMouseDown() {
        if (!this.target.enabled) return;
        this.firstMove = true;
        this.fingers = 1;
    }

    handleMouseMove(event) {
        if (!this.target.enabled) return;
        this.cancelEvent(event);
        this.pointerMove = true;
        this.updateInteraction(event);
        if (this.interaction === this.interactions.DRAG) {
            this.target.handleDrag(event);
            this.target.update();
        }
        this.firstMove = false;
    }

    handleMouseUp(event) {
        if (!this.target.enabled) return;
        if (this.pointerMove) {
            delete this.pointerMove;
        }
        if (!this.interaction && !this.target.inAnimation) {
            this.target.handlePointerZoom(event);
        }
        this.fingers = 0;
        this.updateInteraction(event);
    }
}
